import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  CreditCard,
  Search,
  Loader2,
  DollarSign,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowUpRight,
  TrendingUp,
  Calendar,
} from "lucide-react";

import adminService from "@/services/adminService";

const STATUS_COLORS = {
  CREATED: "bg-slate-500/20 text-slate-300",
  PROCESSING: "bg-blue-500/20 text-blue-300",
  SUCCEEDED: "bg-green-500/20 text-green-300",
  FAILED: "bg-red-500/20 text-red-300",
  CANCELED: "bg-orange-500/20 text-orange-300",
  EXPIRED: "bg-slate-500/20 text-slate-300",
  REFUNDED: "bg-purple-500/20 text-purple-300",
};

const PURPOSE_LABELS = {
  CROWD_CONTRIBUTION: "Crowdfund",
  INDIVIDUAL_FUNDING: "Individual",
  ENCRYPTION_ADDON: "Encryption",
};

export default function AdminPayments() {
  const [loading, setLoading] = useState(true);
  const [intents, setIntents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPurpose, setFilterPurpose] = useState("all");
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await adminService.getPaymentIntents();
      setIntents(Array.isArray(data) ? data : []);
      
      // Calculate stats
      const allIntents = Array.isArray(data) ? data : [];
      const succeededIntents = allIntents.filter(i => i.status === "SUCCEEDED");
      const totalRevenue = succeededIntents.reduce((acc, i) => {
        const currency = i.currency || "USD";
        acc[currency] = (acc[currency] || 0) + (i.total_amount || 0);
        return acc;
      }, {});
      
      setStats({
        total: allIntents.length,
        succeeded: succeededIntents.length,
        failed: allIntents.filter(i => i.status === "FAILED").length,
        pending: allIntents.filter(i => ["CREATED", "PROCESSING"].includes(i.status)).length,
        totalRevenue
      });
    } catch (error) {
      console.error("Error loading payments:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const filteredIntents = intents.filter((intent) => {
    const matchesSearch =
      intent.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      intent.payer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      intent.entity_id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || intent.status === filterStatus;
    const matchesPurpose =
      filterPurpose === "all" || intent.purpose === filterPurpose;
    return matchesSearch && matchesStatus && matchesPurpose;
  });

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Payments</h1>
          <p className="text-slate-400">Monitor payment intents and transactions</p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="border-slate-400 text-white bg-slate-700/50 hover:bg-slate-600"
        >
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.total || 0}</p>
              <p className="text-xs text-slate-400">Total Intents</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.succeeded || 0}</p>
              <p className="text-xs text-slate-400">Succeeded</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.failed || 0}</p>
              <p className="text-xs text-slate-400">Failed</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats?.pending || 0}</p>
              <p className="text-xs text-slate-400">Pending</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Currency */}
      {stats?.totalRevenue && Object.keys(stats.totalRevenue).length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              Total Revenue by Currency
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.totalRevenue).map(([currency, amount]) => (
                <div
                  key={currency}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg"
                >
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <span className="text-white font-bold">
                    {formatCurrency(amount, currency)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by ID, email, entity..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 text-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="CREATED">Created</SelectItem>
            <SelectItem value="PROCESSING">Processing</SelectItem>
            <SelectItem value="SUCCEEDED">Succeeded</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
            <SelectItem value="CANCELED">Canceled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPurpose} onValueChange={setFilterPurpose}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Purpose" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 text-white">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="CROWD_CONTRIBUTION">Crowdfund</SelectItem>
            <SelectItem value="INDIVIDUAL_FUNDING">Individual</SelectItem>
            <SelectItem value="ENCRYPTION_ADDON">Encryption</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Payment Intents List */}
      <div className="space-y-3">
        {filteredIntents.map((intent) => (
          <Card
            key={intent.id}
            className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => {
              setSelectedIntent(intent);
              setShowDrawer(true);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={STATUS_COLORS[intent.status]}>
                      {intent.status}
                    </Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">
                      {PURPOSE_LABELS[intent.purpose] || intent.purpose}
                    </Badge>
                    {intent.encryption_opt_in && (
                      <Badge className="bg-purple-500/20 text-purple-300">
                        +Encryption
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-400">
                      ID: <span className="text-white font-mono">{intent.id?.slice(0, 8)}...</span>
                    </span>
                    {intent.payer_email && (
                      <span className="text-slate-400">
                        Email: <span className="text-white">{intent.payer_email}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-white">
                    {formatCurrency(intent.total_amount, intent.currency)}
                  </p>
                  <p className="text-xs text-slate-400 flex items-center justify-end gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(intent.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredIntents.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center text-slate-500">
            <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No payment intents found</p>
          </CardContent>
        </Card>
      )}

      {/* Payment Detail Drawer */}
      <Sheet open={showDrawer} onOpenChange={setShowDrawer}>
        <SheetContent className="bg-slate-800 border-slate-700 text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Payment Intent Details</SheetTitle>
          </SheetHeader>

          {selectedIntent && (
            <div className="space-y-6 mt-6">
              {/* Status Badge */}
              <div className="flex gap-2">
                <Badge className={STATUS_COLORS[selectedIntent.status]}>
                  {selectedIntent.status}
                </Badge>
                <Badge variant="outline" className="border-slate-600">
                  {PURPOSE_LABELS[selectedIntent.purpose] || selectedIntent.purpose}
                </Badge>
              </div>

              {/* Amount Info */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">Base Amount</p>
                      <p className="text-lg font-bold text-white">
                        {formatCurrency(selectedIntent.amount, selectedIntent.currency)}
                      </p>
                    </div>
                    {selectedIntent.encryption_fee > 0 && (
                      <div>
                        <p className="text-xs text-slate-400">Encryption Fee</p>
                        <p className="text-lg font-bold text-purple-400">
                          {formatCurrency(selectedIntent.encryption_fee, selectedIntent.currency)}
                        </p>
                      </div>
                    )}
                    <div className="col-span-2 pt-2 border-t border-slate-600">
                      <p className="text-xs text-slate-400">Total Amount</p>
                      <p className="text-2xl font-bold text-green-400">
                        {formatCurrency(selectedIntent.total_amount, selectedIntent.currency)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-400">Intent ID</p>
                  <p className="text-white font-mono text-sm break-all">{selectedIntent.id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Entity</p>
                  <p className="text-white">
                    {selectedIntent.entity_type}: {selectedIntent.entity_id?.slice(0, 12)}...
                  </p>
                </div>
                {selectedIntent.payer_email && (
                  <div>
                    <p className="text-xs text-slate-400">Payer Email</p>
                    <p className="text-white">{selectedIntent.payer_email}</p>
                  </div>
                )}
                {selectedIntent.payer_user_id && (
                  <div>
                    <p className="text-xs text-slate-400">Payer User ID</p>
                    <p className="text-white font-mono text-sm">{selectedIntent.payer_user_id}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-400">Provider</p>
                  <p className="text-white">{selectedIntent.provider || "mock"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Created At</p>
                  <p className="text-white">{formatDate(selectedIntent.created_at)}</p>
                </div>
                {selectedIntent.expires_at && (
                  <div>
                    <p className="text-xs text-slate-400">Expires At</p>
                    <p className="text-white">{formatDate(selectedIntent.expires_at)}</p>
                  </div>
                )}
              </div>

              {/* Options */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Encryption Included</span>
                    <span className="text-white">
                      {selectedIntent.encryption_included ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Encryption Opt-in</span>
                    <span className="text-white">
                      {selectedIntent.encryption_opt_in ? "Yes" : "No"}
                    </span>
                  </div>
                  {selectedIntent.idempotency_key && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Idempotency Key</span>
                      <span className="text-white font-mono text-xs">
                        {selectedIntent.idempotency_key.slice(0, 12)}...
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
